import { css } from '@melements/front-components';
import { ButtonProps } from '@melements/front-components/dist/components/button/Button.types';
import { ThemeProps } from 'styled-components';

import { Variables } from '../Theme.types';

export const buttonRuleSets = {
  gray: {
    common: css`
      font-weight: ${p => p.theme.variables.fonts.weight.medium};
      text-transform: uppercase;
    `,
    disabled: css`
      color: ${p => p.theme.variables.customColors.leadingColor.mid};
      background-color: ${p => p.theme.variables.colors.silver};
    `,
    enabled: css`
      color: #ffffff;
      background-color: ${p => p.theme.variables.colors.midGrey};

      &:hover:not(:focus) {
        background-color: ${p => p.theme.variables.colors.darkGrey};
      }

      &:focus {
        background-color: ${p => p.theme.variables.colors.darkGrey};
      }
    `,
  },
  leading: {
    common: css`
      font-weight: ${p => p.theme.variables.fonts.weight.medium};
      text-transform: uppercase;
    `,
    disabled: css`
      color: ${p => p.theme.variables.customColors.leadingColor.mid};
      background-color: ${p => p.theme.variables.colors.silver};
    `,
    enabled: css`
      color: #ffffff;
      background-color: ${p => p.theme.variables.customColors.leadingColor.base};

      &:hover:not(:focus) {
        background-color: ${p => p.theme.variables.customColors.leadingColor.mid};
      }

      &:focus {
        background-color: ${p => p.theme.variables.customColors.leadingColor.dark};
      }
    `,
  },
  'leading-flat': {
    common: css`
      font-weight: ${p => p.theme.variables.fonts.weight.medium};
      text-transform: uppercase;
    `,
    disabled: css`
      color: ${p => p.theme.variables.colors.midGrey};
      background-color: ${p => p.theme.variables.colors.silver};
    `,
    enabled: css`
      color: ${p => p.theme.variables.customColors.leadingColor.base};
      background-color: transparent;

      &:hover:not(:focus) {
        background-color: rgba(0, 0, 0, 0.1);
      }

      &:focus {
        background-color: rgba(0, 0, 0, 0.2);
      }
    `,
  },
  'portal-blue': {
    common: css`
      font-weight: ${p => p.theme.variables.fonts.weight.medium};
      text-transform: uppercase;
    `,
    disabled: css`
      color: ${p => p.theme.variables.customColors.leadingColor.mid};
      background-color: ${p => p.theme.variables.colors.silver};
    `,
    enabled: css<ThemeProps<{ variables: Variables }> & ButtonProps>`
      color: #0088ca;
      background-color: #d8eaf3;

      &:hover:not(:focus) {
        color: white;
        background-color: ${p => p.theme.variables.customColors.lightNavy};
      }

      &:focus {
        color: white;
        background-color: ${p => p.theme.variables.customColors.lightNavy};
      }
    `,
  },
  'portal-dark': {
    common: css<ThemeProps<{ variables: Variables }> & ButtonProps>`
      font-family: ${p => p.theme.variables.typography.primaryFont};
      font-size: ${p => p.theme.variables.fonts.size.smaller1};
      font-weight: ${p => p.theme.variables.fonts.weight.regular};
      min-width: 0;
      min-height: 1rem;
      padding: 0;
      background: transparent;
      border-radius: 0;

      > * {
        &:not(:last-child) {
          margin-right: 1rem;
        }
      }

      .button__icon {
        background-size: contain;
      }
    `,
    disabled: css`
      color: ${p => p.theme.variables.colors.midGrey};
    `,
    enabled: css<ThemeProps<{ variables: Variables }>>`
      color: ${p => p.theme.variables.customColors.leadingColor.base};

      &:hover,
      &:focus {
        color: ${p => p.theme.variables.customColors.leadingColor.dark};
      }
    `,
  },
  'portal-lite': {
    common: css<ThemeProps<{ variables: Variables }> & ButtonProps>`
      font-family: ${p => p.theme.variables.typography.primaryFont};
      font-size: ${p => p.theme.variables.fonts.size.smaller1};
      font-weight: ${p => p.theme.variables.fonts.weight.regular};
      min-width: 0;
      min-height: 1rem;
      padding: 0;
      background: transparent;
      border-radius: 0;

      > * {
        &:not(:last-child) {
          margin-right: 0.5rem;
        }
      }

      .button__icon {
        background-size: contain;
      }
    `,
    disabled: css`
      color: ${p => p.theme.variables.colors.midGrey};
    `,
    enabled: css<ThemeProps<{ variables: Variables }>>`
      color: ${p => p.theme.variables.customColors.lightNavy};

      &:hover,
      &:focus {
        color: ${p => p.theme.variables.customColors.leadingColor.base};
      }
    `,
  },
  'portal-remove': {
    common: css<ThemeProps<{ variables: Variables }> & ButtonProps>`
      font-family: ${p => p.theme.variables.typography.secondaryFont};
      font-size: ${p => p.theme.variables.fonts.size.base};
      font-weight: ${p => p.theme.variables.fonts.weight.semiBold};
      min-width: 0;
      min-height: 1rem;
      padding: 0;
      background: transparent;
      border-radius: none;

      > * {
        &:not(:last-child) {
          margin-right: 0.5rem;
        }
      }

      .button__icon {
        background-size: contain;
      }
    `,
    disabled: css`
      color: ${p => p.theme.variables.colors.midGrey};
    `,
    enabled: css<ThemeProps<{ variables: Variables }>>`
      color: ${p => p.theme.variables.customColors.grayMedium};

      &:hover,
      &:focus {
        color: ${p => p.theme.variables.colors.darkRed};
      }
    `,
  },
  select: {
    common: css`
      font-weight: ${p => p.theme.variables.fonts.weight.medium};
      text-transform: uppercase;
    `,
    disabled: css`
      color: ${p => p.theme.variables.customColors.leadingColor.mid};
      background-color: ${p => p.theme.variables.colors.silver};
    `,
    enabled: css<ThemeProps<{ variables: Variables }> & ButtonProps>`
      transition: opacity 0.3s;
      color: #ffffff;
      background-color: #6a6a72;
      opacity: 0.25;

      &:hover {
        opacity: 0.35;
      }

      &.selected {
        background-color: ${p => p.theme.variables.customColors.green};
        opacity: 0.7;

        &:hover {
          opacity: 0.8;
        }
      }
    `,
  },
};
